import { DAY_ROUTE_COLORS, type AllDayLegs } from '../hooks/useAllDayRoutes';
import type { Activity, ItineraryDay } from '../types';
import type { PinnedVenue } from '../components/map/VenueDetailCard';
import type { ClientLeg } from './legFormatting';
import type { GeoPin } from './pinCollision';

/** Where a stop sits on the map, once its own coords or the route say so. */
export interface StopPosition { lat: number; lng: number }

export interface RouteStop {
  activity: Activity;
  /** 1-based position **within its day** — the panel and the pins share it. */
  num: number;
  /** The saved place behind the stop, when there is one. Only these open a card. */
  venue: PinnedVenue | null;
  position: StopPosition | null;
}

export interface DayRouteGroup {
  /** Index in the original `days[]` array — drives colour and the day filter. */
  dayIdx: number;
  day: ItineraryDay;
  color: string;
  stops: RouteStop[];
  /** `legs[i]` joins `stops[i]` to `stops[i + 1]`. */
  legs: (ClientLeg | null)[];
}

function coordsOf(activity: Activity): StopPosition | null {
  if (activity.latitude == null || activity.longitude == null) return null;
  return { lat: Number(activity.latitude), lng: Number(activity.longitude) };
}

/**
 * A stop with no coordinates of its own still has a position: the route drew a
 * line to it, so the leg's end is where Google actually put it.
 */
function positionFromLegs(
  legs: (ClientLeg | null)[], stopIdx: number,
): StopPosition | null {
  const leg = stopIdx === 0 ? legs[0] : legs[stopIdx - 1];
  if (!leg?.decodedPath.length) return null;
  const point = stopIdx === 0 ? leg.decodedPath[0] : leg.decodedPath[leg.decodedPath.length - 1];
  return { lat: point.lat(), lng: point.lng() };
}

/**
 * The stop's saved place, matched the way the map has always matched it: by id
 * first, then placeId, then name. Only a match gives the stop a detail card.
 */
function venueFor(activity: Activity, pinned: PinnedVenue[]): PinnedVenue | null {
  return pinned.find(v =>
    (activity.venueId && v.id === activity.venueId) ||
    (activity.placeId && v.placeId === activity.placeId) ||
    (activity.venueName && v.name.toLowerCase() === activity.venueName.toLowerCase()),
  ) ?? null;
}

/**
 * The single source of truth for what the unified routes view shows — the panel
 * and the map pins both read it, which is what stops them from disagreeing about
 * order or numbering.
 *
 * Stop order comes from the server's own answer (`activityIds`), never from a
 * local re-sort: the legs were computed against that order, so numbering the
 * stops any other way would draw line `i` between the wrong pair. Days the
 * server skipped (fewer than two routable stops) fall back to `sortOrder`.
 */
export function buildDayRouteGroups(
  days: ItineraryDay[],
  enabledDays: Set<number>,
  allDayLegs: AllDayLegs[],
  pinned: PinnedVenue[],
): DayRouteGroup[] {
  const legsByDayIdx = new Map(allDayLegs.map(d => [d.dayIdx, d]));

  return days.flatMap((day, dayIdx) => {
    if (!enabledDays.has(dayIdx)) return [];

    const dayLegs = legsByDayIdx.get(dayIdx);
    const ordered = dayLegs
      ? dayLegs.activityIds
          .map(id => day.activities.find(a => a.id === id))
          .filter((a): a is Activity => !!a)
      : [...day.activities].sort((a, b) => a.sortOrder - b.sortOrder);

    if (ordered.length === 0) return [];

    const legs = dayLegs?.legs ?? [];
    const stops: RouteStop[] = ordered.map((activity, i) => ({
      activity,
      num: i + 1,
      venue: venueFor(activity, pinned),
      position: coordsOf(activity) ?? positionFromLegs(legs, i),
    }));

    return [{
      dayIdx,
      day,
      color: DAY_ROUTE_COLORS[dayIdx % DAY_ROUTE_COLORS.length],
      stops,
      legs,
    }];
  });
}

/**
 * Two stops at the same spot are the same place: the next day's origin card is a
 * copy of this day's last stop. The panel keeps both rows (the copy is what says
 * "the day continues from here"), but the map must not stack two pins on one
 * point — so dedupe is done here, at render time, not in the model.
 */
export function dedupeOriginPins(groups: DayRouteGroup[]): Set<string> {
  const seen = new Set<string>();
  const hidden = new Set<string>();
  for (const group of groups) {
    for (const stop of group.stops) {
      if (!stop.position) continue;
      const key = `${stop.position.lat.toFixed(5)},${stop.position.lng.toFixed(5)}`;
      if (stop.activity.isOrigin && seen.has(key)) hidden.add(stop.activity.id);
      else seen.add(key);
    }
  }
  return hidden;
}

/**
 * The stops the day could do without — `isOptional`, already in the model and
 * shown in the itinerary and the PDF since it was added.
 *
 * The origin card is excluded for the same reason it is everywhere else: it
 * mirrors the previous day's last stop, so it is not this day's to skip.
 */
export function optionalStopIds(groups: DayRouteGroup[]): Set<string> {
  return new Set(groups.flatMap(group => group.stops.flatMap(stop => (
    stop.activity.isOptional && !stop.activity.isOrigin ? [stop.activity.id] : []
  ))));
}

/**
 * The stops that actually get a pin on the map: everything with a position,
 * minus the origin copies `dedupeOriginPins` folded away.
 *
 * The fan-out that separates overlapping pins reads this list, and that is the
 * whole point of it being one list: a deduped origin is a pin that does not
 * exist, so nothing can push it back onto the map by treating it as a neighbour
 * that needs room.
 */
export function routeStopPins(
  groups: DayRouteGroup[], hiddenOriginPins: Set<string>,
): GeoPin[] {
  return groups.flatMap(group => group.stops.flatMap(stop => (
    stop.position && !hiddenOriginPins.has(stop.activity.id)
      ? [{ id: stop.activity.id, lat: stop.position.lat, lng: stop.position.lng }]
      : []
  )));
}

/**
 * The pin numbers when several days share the map: one count running through
 * them, so a day picks up where the one before it ended. Only drawn pins count —
 * a hidden origin card is the previous day's last pin, already numbered. One day
 * alone keeps its own 1..N (null), the numbers its panel shows.
 */
export function tripPinNums(
  groups: DayRouteGroup[], hiddenPins: Set<string>,
): Map<string, number> | null {
  if (groups.length < 2) return null;
  return new Map(routeStopPins(groups, hiddenPins).map((pin, i) => [pin.id, i + 1]));
}

/**
 * Whether any routed day still has a leg the cache did not answer. ADR-0025:
 * the map's one whole-trip purchase happens only when this is true.
 */
export function hasMissingLegs(allDayLegs: AllDayLegs[]): boolean {
  return allDayLegs.some(day => day.legs.some(leg => leg === null));
}
